import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import React, { useState, useEffect } from "react";
import JustificationDialog from "../../Components/Modals/JustificationDialog";
import { axiosInstance } from "../../utils/axiosInstance";
import { triggerToast } from "../../utils/toast";
import GridTable from "cvmgmt-cube-grid-table";
import { GridTableDataType } from "cvmgmt-cube-grid-table/dist/types/src/GridTable/types";
import { fetchAPIResponse } from "../../services/APIHandler/APIHandler";
import { ColumnFilter, SortingState } from "@tanstack/react-table";
import { Divider, Pagination } from "cprime-foundational-components";
import { formatFilterValue } from "../../utils/formatFilters";
import Button from "../../Components/Common/CprimeButton";
import { useIdToken } from "../../hooks/useAuthSession";
import { MdOutlineFileDownload } from "react-icons/md";
import { downloadBlobFile } from "../../utils/commonUtil";

interface Feature {
  group_name: string;
  featureName: string;
  justification: string | null;
  permissionNames: string;
  status?: string;
}

interface FeaturePermissionJustification {
  group_name: string;
  requested_feature: Feature[];
}

interface RowData {
  access_id: number;
  new_status: string;
  access_owner: string;
  created_dt: string;
  updated_dt?: string;
  feature_permission_justification: FeaturePermissionJustification[];
  approver_comment?: string;
}

function ActiveRoleAssignmentRequests() {
  const [gridData, setGridData] = useState<GridTableDataType | any>();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 25 });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [withdrawRow, setWithdrawRow] = useState<RowData | null>(null);
  const [withDrawPopup, setWithDrawPopup] = useState(false);
  const [filters, setFilters] = useState<ColumnFilter[]>([]);
  const [popupData, setPopupData] = useState<any>(null);
  const [gridRefresh, setGridRefresh] = useState<number>(0);
  const idToken = useIdToken();

  const buildSortingFilterPayload = () => ({
    filter:
      filters?.length > 0
        ? filters?.map((item: any) => formatFilterValue(item?.value, item?.id))
        : [],
    sort: sorting.map((item: any) => {
      return { columnName: item?.id, type: item.desc ? "DESC" : "ASC" };
    }),
    columns: [],
    page: {
      limit: pagination?.rowsPerPage,
      offset: pagination?.page,
    },
  });

  const fetchData = async () => {
    setIsLoading(true);
    const resp = await fetchAPIResponse(
      {
        sortingFilter: buildSortingFilterPayload(),
      },
      `/request/data/myrequest`,
    );
    resp.columns = resp?.columns?.map((column: any) => {
      return {
        ...column,
        size: column?.size ?? 200,
        minSize: column?.minSize ?? 200,
      };
    });
    setGridData(resp);
    setIsLoading(false);
  };

  const openWithdraw = () => setWithDrawPopup((prev) => !prev);

  const handleWithdraw = async (request: any) => {
    try {
      const response = await axiosInstance.post("/request/action", {
        action: "withdrawn",
        accessRoleList: [
          {
            accessRoleId: request?.access_id,
            approvedRoleIds: [],
            deniedRoleIds: [],
            comment: "",
          },
        ],
      });

      if (response?.data?.status?.toLowerCase() === "success") {
        triggerToast("Access Request successfully withdrawn", "success");
        setGridRefresh((prev) => prev + 1);
      }
    } catch (error: any) {
      triggerToast(error?.message, "error");
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setPopupData(null);
  };

  const handleDownloadRequests = async () => {
    try {
      const response = await axiosInstance.post(
        `/onboard/grid-export`,
        {
          serviceUrl: "/request/data/myrequest",
          sortingFilter: buildSortingFilterPayload(),
          fileName: "active_role_assignment_requests",
          sheetName: "active_requests",
        },
        {
          responseType: "blob",
          headers: {
            Accept: "application/vnd.ms-excel",
          },
        },
      );

      const disposition = response?.headers?.["content-disposition"] || "";
      const fileNameMatch = disposition.match(
        /filename\*?=(?:UTF-8''|\")?([^\";]+)/i,
      );
      const fileName = decodeURIComponent(
        (fileNameMatch?.[1] || "active_role_assignment_requests.xlsx").replace(
          /\"/g,
          "",
        ),
      );

      downloadBlobFile(response?.data, fileName);
      triggerToast("Requests downloaded successfully", "success");
    } catch (error: any) {
      triggerToast(error?.message || "Unable to download requests", "error");
    }
  };

  useEffect(() => {
    if (!idToken) {
      return;
    }
    fetchData();
  }, [idToken, filters, sorting, pagination, gridRefresh]);
  useEffect(() => {
    setPagination({
      page: 0,
      rowsPerPage: pagination?.rowsPerPage,
    });
  }, [filters]);

  return (
    <div>
      <Dialog
        open={withDrawPopup}
        // onClose={openWithdraw}
        fullWidth
        maxWidth="sm"
        sx={{
          "& .MuiDialog-paper": { maxWidth: "502px" },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 400,
            fontSize: "24px",
            lineHeight: "32px",
            padding: "24px",
          }}
        >
          Withdraw Request
          <IconButton onClick={openWithdraw}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "0" }}>
          <Stack spacing={3.2} sx={{ padding: "0 24px 24px 24px" }}>
            <span style={{ fontSize: "14px", color: "#58595B" }}>
              Approvers will no longer be able to view and approve this request.
              You will have to submit this request again if needed.
            </span>
            <span style={{ fontSize: "14px", color: "#58595B" }}>
              Would you like to continue?
            </span>
          </Stack>
          <Divider />
          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={2}
            sx={{ padding: "24px" }}
          >
            <Button variant="tertiary" onClick={openWithdraw}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleWithdraw(withdrawRow);
                openWithdraw();
              }}
            >
              Withdraw
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <JustificationDialog
        isStatus
        isSubRequestId
        open={dialogOpen}
        onClose={handleDialogClose}
        row={popupData}
      />
      {idToken && (
        <>
          <GridTable
            style={{
              height: "calc(100vh - 30rem)",
              maxWidth: "calc(100vw - 4rem)",
            }}
            actions={[
              {
                event: "save",
                component: (
                  <IconButton
                    onClick={handleDownloadRequests}
                    id="ActiveRoleAssignmentRequestsDownload"
                    title="Download"
                  >
                    <MdOutlineFileDownload
                      style={{ fontSize: "20px" }}
                      id="active-requests-icon-download"
                    />
                  </IconButton>
                ),
              },
            ]}
            data={gridData}
            customPayload={true}
            isPagination={true}
            isCustomizeColumns={false}
            isSorting={true}
            isFiltering={true}
            title={"Access Status"}
            refresh={gridRefresh}
            filters={filters}
            sorting={sorting}
            setSorting={setSorting}
            setFilters={setFilters}
            cellActions={[
              {
                event: "Withdraw Request",
                callback: (_event, data) => {
                  setWithdrawRow(data?.row?.original);
                  setWithDrawPopup(true);
                },
                icon: false,
              },
            ]}
            onRowClick={(_event: any, row: any) => {
              setDialogOpen(true);
              setPopupData(row?.original?.feature_permission_justification);
            }}
            showLoader={false}
            loading={isLoading}
          />
          <Pagination
            sx={{ width: "100%" }}
            {...{
              ...pagination,
              count: gridData?.totalCount ?? 0,
              rowsPerPageOptions: [25, 50, 100, 250],
              onPageChange: (e, number) => {
                setPagination({ ...pagination, page: number });
              },
              onRowsPerPageChange: (evt) => {
                setPagination({
                  page: 0,
                  rowsPerPage: Number(evt?.target?.value),
                });
              },
            }}
          />
        </>
      )}
    </div>
  );
}

export default ActiveRoleAssignmentRequests;
